/* Activity feed (Phase 6) — live bidding pulses from the activity bus plus the
   signed-in user's notifications. Rendered inside the floating Activity Center. */
import { Bell, Gavel } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { timeAgo } from "@/components/bidding/BidHistory";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useActivity } from "@/lib/activity";
import { formatCurrency } from "@/lib/intl";
import { notificationText } from "@/pages/account/AccountNotifications";

const SectionLabel = ({ children }) => (
  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/35 px-1">{children}</span>
);

export const ActivityFeed = ({ open }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();
  const live = useActivity();
  const [notifs, setNotifs] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    api
      .get("/notifications")
      .then(({ data }) => {
        setNotifs(data.items || []);
        if (data.unread > 0) api.post("/notifications/read-all").catch(() => {});
      })
      .catch(() => setNotifs([]));
  }, [open, user]);

  const tsAgo = (ts) => timeAgo(new Date(ts).toISOString(), t);
  const empty = live.length === 0 && (!user || (notifs || []).length === 0);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4" data-testid="activity-feed">
      {live.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="activity-live-section">
          <SectionLabel>{t("ai.liveActivity")}</SectionLabel>
          <ol className="flex flex-col divide-y divide-black/5">
            {live.map((a) => (
              <li key={a.id} className="py-2.5 flex items-center gap-3" data-testid="activity-live-item">
                <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                  {a.kind === "bid" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1D4D3B]/40" />}
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${a.kind === "bid" ? "bg-[#1D4D3B]" : "bg-black/30"}`} />
                </span>
                <span className="flex-1 text-sm text-[#0A0A0A] leading-snug min-w-0">
                  {a.kind === "closed"
                    ? t("ai.lotClosed", { number: a.lot_number })
                    : t("ai.newBid", { number: a.lot_number })}
                  {a.amount_cents != null && (
                    <span className="font-semibold tabular-nums"> {formatCurrency(a.amount_cents, a.currency, locale)}</span>
                  )}
                </span>
                <span className="text-[11px] text-[#737373] tabular-nums shrink-0">{tsAgo(a.ts)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {user && (notifs || []).length > 0 && (
        <div className="flex flex-col gap-1" data-testid="activity-notifications-section">
          <SectionLabel>{t("ai.notifications")}</SectionLabel>
          <ol className="flex flex-col divide-y divide-black/5">
            {notifs.slice(0, 12).map((n) => (
              <li key={n.id} className="py-2.5 flex items-start gap-3" data-testid="activity-notification-item">
                <Bell className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${n.read ? "text-black/25" : "text-[#1D4D3B]"}`} aria-hidden="true" />
                <span className="flex-1 text-sm text-[#0A0A0A] leading-snug min-w-0">{notificationText(n, t, locale)}</span>
                <span className="text-[11px] text-[#737373] tabular-nums shrink-0">{timeAgo(n.created_at, t)}</span>
              </li>
            ))}
          </ol>
          <Link to="/account/notifications" className="text-xs font-medium text-[#1D4D3B] hover:underline underline-offset-4 px-1 pt-1 w-fit" data-testid="activity-all-notifications">
            {t("ai.allNotifications")}
          </Link>
        </div>
      )}

      {empty && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6 py-10" data-testid="activity-empty">
          <span className="w-11 h-11 rounded-full bg-black/5 flex items-center justify-center" aria-hidden="true">
            <Gavel className="w-5 h-5 text-black/35" />
          </span>
          <p className="text-sm text-[#737373] leading-relaxed">{t("ai.activityEmpty")}</p>
          {!user && (
            <Link to="/signin" className="text-xs font-medium text-[#1D4D3B] hover:underline underline-offset-4" data-testid="activity-signin">
              {t("ai.signInNotifications")}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
